import Link from 'next/link';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import { StarsV2 } from '../Stars.v2';
import { ReviewStarsProps } from './ReviewStars.types';

const TOTAL_STARS = '/5';

export const ReviewStars = ({
  value,
  max,
  reviews,
  microcopy,
  showReviewsLink = true,
  variant,
  className,
}: ReviewStarsProps) => {
  const reviewsThreshold = 5;
  const notEnoughReviews = reviews < reviewsThreshold;
  const REVIEW_HASH = '#reviews';

  const textColorClass = notEnoughReviews ? 'text-grey-450' : 'text-grey-800';

  const reviewsText =
    notEnoughReviews && microcopy.notEnoughReviews
      ? microcopy.notEnoughReviews
      : microcopy.reviews
        ? interpolateStringWithData(microcopy.reviews, {
            rating: reviews,
          })
        : '';

  const ariaLabel = interpolateStringWithData(microcopy.ariaLabel, {
    reviews: value,
    max,
  });

  const renderNotEnoughReviews = (extraClassName?: string) => (
    <p
      className={c(
        'text-sm font-medium tracking-[-0.028px]',
        textColorClass,
        extraClassName
      )}
    >
      {reviewsText}
    </p>
  );

  const renderReviewStarsLarge = () => (
    <div className={c('text-center md:text-left', className)}>
      <div className={c('mb-4 tracking-[-0.028px]', textColorClass)}>
        <span className="text-7xl font-bold tracking-[-0.264px]">{value}</span>
        <span className="text-2xl font-bold tracking-[-0.072px]">
          {TOTAL_STARS}
        </span>
      </div>

      <StarsV2
        width={24}
        height={24}
        value={value}
        max={max}
        className={c(
          'mb-2 items-center md:items-start',
          notEnoughReviews ? '[&_svg]:text-grey-450' : '[&_svg]:text-grey-900'
        )}
        ariaLabel={ariaLabel}
      />

      {reviewsText && renderNotEnoughReviews()}
    </div>
  );

  if (variant === 'small') {
    if (notEnoughReviews) {
      if (!reviewsText) return null;

      return (
        <p
          className={c(
            'text-sm font-medium tracking-[-0.028px]',
            textColorClass,
            className
          )}
        >
          {reviewsText}
        </p>
      );
    }

    return (
      <div className={c('flex items-center text-sm', className)}>
        <span className="mr-1 font-normal tracking-[-0.028px] text-grey-900">
          {value}
        </span>

        <StarsV2 value={value} max={max} ariaLabel={ariaLabel} />

        {showReviewsLink && reviewsText && (
          <Link
            className="ml-1 tracking-[-0.028px] text-blue no-underline"
            href={REVIEW_HASH}
          >
            {`(${reviewsText})`}
          </Link>
        )}
      </div>
    );
  }

  return renderReviewStarsLarge();
};
